import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  MercadoPagoService,
  getMercadoPagoConfig,
} from "../../../shared/services";
import { useDeviceId } from "../../../shared/hooks/useDeviceId";
import "./Styles.css";

const MercadoPagoCVVPage = () => {
  const { cardId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [securityError, setSecurityError] = useState<string | null>(null);
  const mpInstance = useRef<any>(null);
  const fields = useRef({
    securityCode: null,
  });
  const [showErrors, setShowErrors] = useState<{
    show: boolean;
    firstInstance: boolean;
  }>({ show: false, firstInstance: true });
  const cvvFieldVoidMessage = "CVV requerido";
  const [ErrorMessageSecurityCode, setErrorMessageSecurityCode] = useState<
    string | null
  >(cvvFieldVoidMessage);
  const [isHoveringSecurityCode, setIsHoveringSecurityCode] = useState(false);

  // Hook para obtener el Device ID
  const {
    deviceId,
    isLoading: isDeviceIdLoading,
    error: deviceIdError,
  } = useDeviceId();

  useEffect(() => {
    if (!cardId) {
      setMessage("No se pudo cargar el formulario");
      return;
    }

    const initializeComponent = async () => {
      try {
        // Esperar a que el Device ID esté disponible
        if (isDeviceIdLoading) {
          return; // Esperar al siguiente render
        }

        // ❌ BLOQUEAR si no hay Device ID
        if (!deviceId) {
          setSecurityError(
            "Error de seguridad: No se pudo obtener el Device ID. Por favor, recarga la página."
          );
          console.error(
            "❌ BLOQUEADO: No se puede cargar MercadoPago sin Device ID"
          );
          return;
        }

        // ❌ BLOQUEAR si hay error en Device ID
        if (deviceIdError) {
          setSecurityError(
            `Error de seguridad: ${deviceIdError}. Por favor, recarga la página.`
          );
          console.error("❌ BLOQUEADO: Error en Device ID:", deviceIdError);
          return;
        }

        // ✅ Solo continuar si tenemos Device ID
        console.log("✅ Device ID disponible, inicializando MercadoPago...");
        const config = getMercadoPagoConfig(deviceId);
        await MercadoPagoService.getInstance().initialize(config);
        initializeMercadoPago(cardId);
      } catch (error) {
        console.error("Error inicializando MercadoPago:", error);
        setMessage("No se pudo cargar el formulario");
      }
    };

    initializeComponent();

    return () => {
      unmountFields();
    };
  }, [deviceId, isDeviceIdLoading, deviceIdError, cardId]);

  const initializeMercadoPago = (id: string) => {
    try {
      mpInstance.current = MercadoPagoService.getInstance().getInstance();

      const checkDOM = setInterval(() => {
        const container = document.getElementById("mp-security-code");
        if (container) {
          clearInterval(checkDOM);
          mountSecurityCodeField(id);
          setSdkReady(true);
          setFormReady(true);
        }
      }, 100);
    } catch (error) {
      console.error("Error inicializando MercadoPago:", error);
      setMessage("No se pudo cargar el formulario");
    }
  };

  const mountSecurityCodeField = (id: string) => {
    try {
      const securityCodeField = mpInstance.current.fields
        .create("securityCode", {
          placeholder: "123",
          cardId: id,
        })
        .mount("mp-security-code");
      securityCodeField.on("focus", () => {
        setIsHoveringSecurityCode(true);
      });
      securityCodeField.on("blur", () => {
        setIsHoveringSecurityCode(false);
      });
      securityCodeField.on("validityChange", (data: any) => {
        const { errorMessages } = data;
        if (errorMessages.length > 0) {
          const invalidValueError = errorMessages.find(
            (err: any) => err.cause === "invalid_value"
          );

          if (invalidValueError) {
            setErrorMessageSecurityCode(cvvFieldVoidMessage);
          } else {
            const invalidLengthError = errorMessages.find(
              (err: any) => err.cause === "invalid_length"
            );

            if (invalidLengthError) {
              setErrorMessageSecurityCode("El CVV debe tener 3 o 4 dígitos");
            } else {
              setErrorMessageSecurityCode("CVV inválido");
            }
          }
          if (!showErrors.firstInstance) {
            setShowErrors({ ...showErrors, show: true });
          }
        } else {
          setErrorMessageSecurityCode(null);
        }
      });

      fields.current.securityCode = securityCodeField;
    } catch (error) {
      console.error("Error montando campo de CVV:", error);
      setMessage("No se pudo cargar el formulario");
    }
  };

  const unmountFields = () => {
    Object.values(fields.current).forEach((field: any) => {
      if (field && typeof field.unmount === "function") {
        field.unmount();
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sdkReady || isLoading) return;

    let hasError = false;

    if (ErrorMessageSecurityCode) {
      setShowErrors({ show: true, firstInstance: false });
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const token = await mpInstance.current.fields.createCardToken({
        cardId: cardId,
      });

      window.ReactNativeWebView.postMessage(JSON.stringify({ token }));
    } catch (error: any) {
      console.error("Error al crear token:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mostrar error de seguridad si no hay Device ID
  if (securityError) {
    return (
      <div
        className="mp-container-ios"
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="text-center">
            <div className="mb-4 text-6xl text-red-600">🔒</div>
            <h2 className="mb-2 text-xl font-bold text-red-800">
              Error de Seguridad
            </h2>
            <p className="mb-4 text-red-700">{securityError}</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded bg-red-600 px-6 py-2 text-white hover:bg-red-700"
            >
              Recargar Página
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mp-container-ios"
      style={{
        height: "100vh", // Usar viewport height completo
        maxHeight: "100vh", // Asegurar que no exceda la pantalla
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        maxWidth: "500px",
        margin: "auto",
        backgroundColor: "transparent",
        paddingTop: "20px",
        paddingBottom: "20px",
        paddingLeft: "16px",
        paddingRight: "16px",
        boxSizing: "border-box", // Incluir padding en el cálculo de altura
      }}
    >
      {!formReady && <div className="loading"></div>}

      <div className={formReady ? "mp-content-ios" : "hidden"}>
        {message && (
          <div
            className={`mb-4 rounded-lg p-3 text-center ${
              message.includes("Error")
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mp-form-ios">
          <div className="mp-input-general">
            <label
              htmlFor="mp-security-code"
              className={`mp-label-general ${
                ErrorMessageSecurityCode && showErrors.show
                  ? "mp-text-error"
                  : isHoveringSecurityCode
                  ? "mp-label-general-hover"
                  : "mp-text-normal"
              }`}
            >
              CVV
            </label>
            <div
              id="mp-security-code"
              className={`${
                ErrorMessageSecurityCode && showErrors.show
                  ? "error-border"
                  : isHoveringSecurityCode
                  ? "hover-border"
                  : ""
              }`}
            />
            {ErrorMessageSecurityCode && showErrors.show && (
              <div className="mp-message-error">{ErrorMessageSecurityCode}</div>
            )}
          </div>

          <div className="mp-buttons-container">
            <button
              type="submit"
              disabled={!sdkReady || isLoading}
              className="mp-button"
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Procesando...
                </>
              ) : (
                "Confirmar"
              )}
            </button>
            <button
              type="button"
              disabled={isLoading}
              onClick={() => {
                if (window.ReactNativeWebView) {
                  window.ReactNativeWebView?.postMessage(
                    JSON.stringify({ action: "cancel" })
                  );
                }
              }}
              className="mp-button-cancel"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MercadoPagoCVVPage;

declare global {
  interface Window {
    MercadoPago: any;
    ReactNativeWebView: {
      postMessage: (message: string) => void;
    };
  }
}
